import gsap from 'gsap';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { Container } from '@pixi/react';
import { isMobile, handleSpineResize, HandleSpineResizeResult } from '../../../../../../helpers';
import { BoostedJackpotWheel } from './boostedJackpotWheel';
import { BoostedJackpotParticles } from '../boostedJackpotParticles';
import { BoostedJackpotWheelTimer } from '../boostedJackpotWheelTimer';
import { Spine } from '../../../../../components';
import { useStore } from '../../../../../../hooks';
import { soundService, SoundTypes } from '../../../../../../services/soundService';

const playStartThenIdle = (spine: Spine, fallbackIdle = 'loop') => {
  if (spine.state?.hasAnimation('start')) {
    spine.play('start', false);
    spine.addListener(
      'complete',
      () => {
        if (spine.state?.hasAnimation('idle')) {
          spine.play('idle', true);
          return;
        }
        if (spine.state?.hasAnimation(fallbackIdle)) {
          spine.play(fallbackIdle, true);
        }
      },
      null,
      '',
      true
    );
    return;
  }
  if (spine.state?.hasAnimation('idle')) {
    spine.play('idle', true);
    return;
  }
  if (spine.state?.hasAnimation(fallbackIdle)) {
    spine.play(fallbackIdle, true);
  }
};

const playAppearThenLoop = (spine: Spine) => {
  if (spine.state?.hasAnimation('appear')) {
    spine.play('appear', false);
    spine.addListener(
      'complete',
      () => {
        if (spine.state?.hasAnimation('loop')) {
          spine.play('loop', true);
          return;
        }

        if (spine.state?.hasAnimation('idle')) {
          spine.play('idle', true);
        }
      },
      null,
      '',
      true
    );
    return;
  }

  if (spine.state?.hasAnimation('loop')) {
    spine.play('loop', true);
    return;
  }

  playStartThenIdle(spine, 'animation');
};

export const BoostedJackpotWheelWrapper = observer(({
  cursor,
  wheelBtn,
  sectors = 6,
  wheelValues,
  wheelBorder,
  amountSlotRef,
  congratSlotRef,
  bgSpineRef,
  buttonSlotRef,
}: any) => {
  const wheelContainerRef: any = useRef(null);
  const wheelOpenAnimationRef = useRef<gsap.core.Timeline | null>(null);
  const wheelThemeBgRef: any = useRef(null);
  const wheelThemeBgSpineRef = useRef<Spine | null>(null);
  const timeSlotRef: any = useRef(null);
  const particlesCoinJumpSlotRef: any = useRef(null);
  const particlesWheelSlotRef: any = useRef(null);
  const spineResizeRef = useRef<HandleSpineResizeResult | null>(null);
  const wheelResizeScaleRef = useRef(1);
  // For each child we move into a spine slot, remember its original React-Pixi parent so
  // we can restore it on cleanup. Otherwise React-Pixi's removeChild fails ("must be a child of caller").
  const slotMountsRef = useRef<Array<{ child: any, originalParent: any }>>([]);
  const [showCircleSparkles, setShowCircleSparkles] = useState(false);

  const { boostedJackpotStore } = useStore();
  const { hasBoostedJackpot, isWheel, isBoostedPopupOpen, isBoostedJackpotWinPopup, isWheelZoomOut } = boostedJackpotStore;

  const playEndOnAllSpines = (node: any) => {
    if (!node) return;

    if (typeof node.play === 'function' && node.state?.hasAnimation?.('disappear')) {
      try {
        node.play('disappear', false);
      } catch {
        // Ignore individual spine disappear animation errors.
      }
    } else if (typeof node.play === 'function' && node.state?.hasAnimation?.('end')) {
      try {
        node.play('end', false);
      } catch {
        // Ignore individual spine end animation errors.
      }
    }

    if (Array.isArray(node.children) && node.children.length) {
      node.children.forEach((child: any) => playEndOnAllSpines(child));
    }
  };

  // Mount a React-Pixi container into a spine slot via getSlot().addChild(...).
  // Skips if already mounted into the same slot; tracks original parent for restore on unmount.
  const mountIntoSlot = (spine: Spine, slotName: string, child: any) => {
    if (!spine || !child) return;
    const slot: any = spine.getSlot?.(slotName);
    if (!slot) return;
    if (child.parent === slot) return;

    const alreadyTracked = slotMountsRef.current.some((entry) => entry.child === child);
    if (!alreadyTracked) {
      slotMountsRef.current.push({ child, originalParent: child.parent ?? null });
    }

    try {
      slot.addChild(child);
    } catch {
      // Defensive: addChild may throw if the slot was just destroyed mid-frame.
    }
  };

  useEffect(() => {
    if (!wheelThemeBgRef.current) return;

    const wheelThemeBg: Spine = new Spine({ key: 'boostedWheelThemeBg' });
    playAppearThenLoop(wheelThemeBg);
    wheelThemeBgSpineRef.current = wheelThemeBg;
    if (bgSpineRef) bgSpineRef.current = wheelThemeBg;
    wheelThemeBgRef.current.addChild(wheelThemeBg);

    // Resize background/noScale bones based on window size; mobile design is 540x960.
    spineResizeRef.current = handleSpineResize(wheelThemeBg, [
      { boneName: 'backgound', scaleMode: 'max' },
      { boneName: 'background', scaleMode: 'max' },
      { boneName: 'noScale', scaleMode: 'min' },
    ], isMobile()
      ? { designWidth: 540, designHeight: 960 }
      : { designWidth: 1441.82, designHeight: 856.73 }
    );

    // Rays is a fresh unmanaged spine; safe to mount inside _raysSlot.
    const wheelThemeRays: Spine = new Spine({ key: 'boostedWheelThemeRays' });
    playStartThenIdle(wheelThemeRays, 'loop');
    const raysSlot: any = wheelThemeBg.getSlot?.('_raysSlot');
    if (raysSlot) raysSlot.addChild(wheelThemeRays as any);

    // Initial slot mounts for refs already available.
    mountIntoSlot(wheelThemeBg, '_buttonSlot', buttonSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_timeSlot', timeSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_particlesCoinJumpSlot', particlesCoinJumpSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_particlesWheelSlot', particlesWheelSlotRef.current);
    [particlesWheelSlotRef.current, particlesCoinJumpSlotRef.current].forEach((el) => {
      if (el?.scale) el.scale.set(wheelResizeScaleRef.current);
    });
    mountIntoSlot(wheelThemeBg, '_amountSlot', amountSlotRef?.current);
    mountIntoSlot(wheelThemeBg, '_congratSlot', congratSlotRef?.current);

    return () => {
      // Restore React-Pixi owned children to their original parents BEFORE React-Pixi
      // commits the unmount. Guard against destroyed parents to avoid setting _parentID on null.
      slotMountsRef.current.forEach(({ child, originalParent }) => {
        if (!child) return;
        const safeParent = originalParent && !originalParent.destroyed && originalParent.transform
          ? originalParent
          : null;
        try {
          if (safeParent) {
            safeParent.addChild(child);
          } else if (child.parent) {
            child.parent.removeChild(child);
          }
        } catch {
          // Best-effort cleanup; ignore reconciliation races during teardown.
        }
      });
      slotMountsRef.current = [];

      spineResizeRef.current?.dispose();
      spineResizeRef.current = null;
      if (bgSpineRef) bgSpineRef.current = null;
      wheelThemeBgSpineRef.current = null;
    };
  }, []);

  // Re-mount conditionally-rendered slot children (wheel, amount) when their refs become available.
  useEffect(() => {
    const wheelThemeBg = wheelThemeBgSpineRef.current;
    if (!wheelThemeBg) return;

    mountIntoSlot(wheelThemeBg, '_wheelSlot', wheelContainerRef.current);
    mountIntoSlot(wheelThemeBg, '_buttonSlot', buttonSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_timeSlot', timeSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_particlesCoinJumpSlot', particlesCoinJumpSlotRef.current);
    mountIntoSlot(wheelThemeBg, '_particlesWheelSlot', particlesWheelSlotRef.current);
    [particlesWheelSlotRef.current, particlesCoinJumpSlotRef.current].forEach((el) => {
      if (el?.scale) el.scale.set(wheelResizeScaleRef.current);
    });
    mountIntoSlot(wheelThemeBg, '_amountSlot', amountSlotRef?.current);
    mountIntoSlot(wheelThemeBg, '_congratSlot', congratSlotRef?.current);
  }, [amountSlotRef, congratSlotRef, isWheel, isWheelZoomOut, showCircleSparkles]);
  // Mirror the noScale bone formula from handleSpineResize for the wheel container.
  useEffect(() => {
    const designWidth = isMobile() ? 540 : 1441.82;
    const designHeight = isMobile() ? 960 : 856.73;
    const MIN_SCALE = 0.8;
    const MAX_SCALE = 1.0;

    const applyWheelResize = () => {
      const raw = Math.min(window.innerWidth / designWidth, window.innerHeight / designHeight);
      const scale = Math.min(Math.max(raw, MIN_SCALE), MAX_SCALE);
      wheelResizeScaleRef.current = scale;
      const container = wheelContainerRef.current;
      if (container?.scale && !wheelOpenAnimationRef.current?.isActive()) {
        container.scale.set(scale);
      }
      [particlesWheelSlotRef.current, particlesCoinJumpSlotRef.current].forEach((el) => {
        if (el?.scale) el.scale.set(scale);
      });
    };

    applyWheelResize();
    window.addEventListener('resize', applyWheelResize);
    return () => window.removeEventListener('resize', applyWheelResize);
  }, []);

  // Open animation
  useEffect(() => {
    const wheelContainer = wheelContainerRef.current;
    const wheelScale = wheelContainer?.scale;

    if (wheelContainer && wheelScale && isWheel) {
      soundService.play(SoundTypes.OpenWheel);
      wheelOpenAnimationRef.current?.kill();
      setShowCircleSparkles(false);
      const rs = wheelResizeScaleRef.current;
      wheelContainer.alpha = 0;
      wheelScale.set(isMobile() ? 0.75 * rs : 0.8 * rs);

      wheelOpenAnimationRef.current = gsap.timeline()
        .to(wheelContainer, {
          alpha: 1,
          duration: 0.35,
          ease: 'power1.out',
        }, 0)
        .to(wheelScale, {
          x: isMobile() ? 0.9 * rs : rs,
          y: isMobile() ? 0.9 * rs : rs,
          duration: 0.8,
          ease: 'power2.out',
          onComplete: () => {
            setShowCircleSparkles(true);
          },
        }, 0);
    } else if (!isWheel) {
      setShowCircleSparkles(false);
    };

    return () => {
      wheelOpenAnimationRef.current?.kill();
      wheelOpenAnimationRef.current = null;
      if (wheelContainer) gsap.killTweensOf(wheelContainer);
      if (wheelScale) gsap.killTweensOf(wheelScale);
    };
  }, [wheelContainerRef, isWheel]);

  // Fade the timer out together with the wheel when the wheel starts closing.
  useEffect(() => {
    const el = timeSlotRef.current;
    if (!el) return;
    if (isWheelZoomOut) {
      // Match the minimum wheel close duration (420 ms) so both disappear together.
      gsap.to(el, { alpha: 0, duration: 0.42, ease: 'power1.in' });
    } else {
      gsap.killTweensOf(el);
      el.alpha = 1;
    }
    return () => { gsap.killTweensOf(el); };
  }, [isWheelZoomOut]);

  // Play win animation on background spine when congrats popup opens.
  useEffect(() => {
    const spine = wheelThemeBgSpineRef.current;
    if (!spine) return;
    if (isBoostedJackpotWinPopup && spine.state?.hasAnimation?.('win')) {
      spine.play('win', false);
    }
  }, [isBoostedJackpotWinPopup]);

  // Background and rays must remain visible through the congrats popup phase.
  // Wheel-specific end animations are handled inside BoostedJackpotWheel's isWheelZoomOut effect.
  // (no-op — do not call playEndOnAllSpines on the background here)

  // Render scene as soon as boosted jackpot is triggered; do not wait only for popup-open flag.
  const shouldRenderScene = hasBoostedJackpot || isBoostedPopupOpen || isWheelZoomOut || isBoostedJackpotWinPopup;
  if (!shouldRenderScene) return null;

  return (
    <>
      {/* Background spine. Position & scale come entirely from spine bones (handleSpineResize). */}
      <Container ref={wheelThemeBgRef} />

      {/* Slot holders. No position configs - positioning comes from spine slot bones. */}
      {/* buttonSlotRef must live HERE (wrapper level) not inside BoostedJackpotWheel.
          If it were rendered inside the conditionally-rendered wheelContainerRef subtree,
          React-Pixi would call removeChild(wheelContainerRef, buttonSlot) when the wheel
          closes — but buttonSlot was moved to _buttonSlot by mountIntoSlot, so the call
          throws. Keeping it here means it never unmounts while in the spine slot. */}
      <Container ref={buttonSlotRef} />
      {/* Amount slot: always rendered so amountSlotRef.current is a valid Container that
          gets mounted into _amountSlot of the background spine. The congrats popup adds
          the amount spine into it when isBoostedJackpotWinPopup becomes true. */}
      <Container ref={amountSlotRef} />
      <Container ref={congratSlotRef} />
      <Container ref={timeSlotRef} visible={!!(isWheel || isWheelZoomOut) && !isBoostedJackpotWinPopup}>
        <BoostedJackpotWheelTimer />
      </Container>
      <Container ref={particlesCoinJumpSlotRef} />
      <Container ref={particlesWheelSlotRef} />

      {(showCircleSparkles || isBoostedJackpotWinPopup) && (
        <BoostedJackpotParticles
          showCoinCelebration={isBoostedJackpotWinPopup}
          zoomOutBackground={isWheelZoomOut}
          wheelParticlesParentRef={particlesWheelSlotRef}
          coinParticlesParentRef={particlesCoinJumpSlotRef}
        />
      )}

      {/* wheelContainerRef must be always rendered (never conditionally unmounted) so that
          React-Pixi never calls removeChild on its spine-slot parent while it is still
          mounted there. Visibility is controlled by the `visible` prop instead. */}
      <Container ref={wheelContainerRef} visible={!!(isWheel || isWheelZoomOut)}>
        {(isWheel || isWheelZoomOut) && (
          <BoostedJackpotWheel
            cursor={cursor}
            sectors={sectors}
            bgSpineRef={bgSpineRef}
            wheelBtn={wheelBtn}
            timeSlotRef={timeSlotRef}
            wheelBorder={wheelBorder}
            buttonSlotRef={buttonSlotRef}
            wheelValues={wheelValues}
          />
        )}
      </Container>
    </>
  );
});
